import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import ColorizeIcon from "@mui/icons-material/Colorize";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectEnvironmentInteractionPoints } from "../../../app/slices/EnvironmentSlice";
import {
  openSequencePositionPicker,
  selectSequencePositionPicker,
} from "../../../app/slices/GlobalSlice";
import {
  selectActors,
  selectSelectedTake3D,
} from "../../../app/slices/TrainingSlice";
import ActorAvatar from "../../../components/actors/ActorAvatar";
import PiCreatorDropdown from "../../../components/PiCreatorDropdown/PiCreatorDropdown";
import PiCreatorDropdownItem from "../../../components/PiCreatorDropdown/PiCreatorDropdownItem";
import useSituationHelper from "../../../hooks/SituationHelper.hook";
import PropIcon from "../../../icons/PropIcon";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import { useTranslation } from "react-i18next";
import { isNoLookat, NO_LOOKAT } from "../../../models/NoLookat";
import {
  getActorPositionIdInTake,
  getEnvironmentPositionContexts,
  getSituationPositionId,
} from "../EnvironmentPositionContext";
import {
  NODE_PICK_BUTTON_ACTIVE,
  NODE_PICK_BUTTON_BASE,
  NODE_PICK_BUTTON_IDLE,
} from "../components/nodeStyles";

export default function LookatTargetDropdown(props: {
  label: string;
  nodeId: string;
  targetIndex?: number;
  selectedTargetId: string;
  onClick: (targetId: string) => void;
  /** Adds "Keep current" (empty value): whatever the actor was looking at carries on. */
  allowKeepCurrent?: boolean;
  /** Adds "No one" (NO_LOOKAT): the actor stops looking. */
  allowNoActor?: boolean;
  disabled?: boolean;
  /**
   * The actor doing the looking. The position it occupies in the current take
   * is hidden from the options so an actor can never look at itself.
   */
  excludeActorId?: string;
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const actors = useSelector(selectActors);
  const take = useSelector(selectSelectedTake3D);
  const allPositions = useSelector(selectEnvironmentInteractionPoints);
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);
  const { getSituationMetadata } = useSituationHelper();

  const positionContexts = useMemo(
    () =>
      getEnvironmentPositionContexts(
        allPositions ?? [],
        take?.situations,
        getSituationMetadata,
      ),
    [allPositions, getSituationMetadata, take?.situations],
  );

  const actorPositions = positionContexts.flatMap((context) =>
    context.actorPositions.map((position) => ({
      position,
      targetId: getSituationPositionId(context, position.id),
    })),
  );
  const propPositions = positionContexts.flatMap((context) =>
    context.propPositions.map((position) => ({
      position,
      targetId: getSituationPositionId(context, position.id),
    })),
  );
  const cameraPositions = positionContexts.flatMap((context) =>
    context.cameraPositions.map((position) => ({
      position,
      targetId: getSituationPositionId(context, position.id),
    })),
  );

  const actorByPositionId = useMemo(() => {
    return new Map(
      (take?.actors ?? [])
        .map((actorState) => {
          const actor = actors.find(
            (candidate) => candidate.id === actorState.actorId,
          );
          if (!actor) return undefined;
          return [actorState.positionId, actor] as const;
        })
        .filter((entry): entry is readonly [string, (typeof actors)[number]] =>
          Boolean(entry),
        ),
    );
  }, [actors, take?.actors]);

  const excludedPositionId = getActorPositionIdInTake(
    take,
    props.excludeActorId,
  );
  const selectableActorPositions = actorPositions.filter(
    ({ targetId }) => targetId !== excludedPositionId,
  );

  const selectedActorPosition = actorPositions.find(
    (entry) => entry.targetId === props.selectedTargetId,
  );
  const selectedActor = selectedActorPosition
    ? actorByPositionId.get(selectedActorPosition.targetId)
    : undefined;
  const selectedPropPosition = propPositions.find(
    (entry) => entry.targetId === props.selectedTargetId,
  );
  const selectedCameraPosition = cameraPositions.find(
    (entry) => entry.targetId === props.selectedTargetId,
  );

  const keepCurrentLabel = String(t("chip_select.keep_current"));
  const noActorLabel = String(t("chip_select.no_actor"));
  const isNoActor = isNoLookat(props.selectedTargetId);
  const isKeepCurrent = !!props.allowKeepCurrent && !props.selectedTargetId;
  const emptyLabel = isNoActor
    ? noActorLabel
    : isKeepCurrent
      ? keepCurrentLabel
      : "No target";

  const dropdownItems = [
    ...(props.allowKeepCurrent
      ? [
          <PiCreatorDropdownItem
            key="keep-current"
            text={keepCurrentLabel}
            icon={<RemoveOutlinedIcon fontSize="inherit" color="inherit" />}
            onClick={() => props.onClick("")}
          />,
        ]
      : []),
    ...(props.allowNoActor
      ? [
          <PiCreatorDropdownItem
            key="no-actor"
            text={noActorLabel}
            icon={<PersonOffOutlinedIcon fontSize="inherit" color="inherit" />}
            onClick={() => props.onClick(NO_LOOKAT)}
          />,
        ]
      : []),
    ...selectableActorPositions.map(({ position: actorPosition, targetId }) => {
      const actor = actorByPositionId.get(targetId);
      return (
        <PiCreatorDropdownItem
          key={`actor-position-${targetId}`}
          text={actor?.workingName ?? actorPosition.name ?? "Actor Position"}
          icon={
            <ActorAvatar theme="creator-selected" actor={actor} size="24" />
          }
          onClick={() => props.onClick(targetId)}
        />
      );
    }),
    ...propPositions.map(({ position: propPosition, targetId }) => {
      return (
        <PiCreatorDropdownItem
          key={`prop-position-${targetId}`}
          text={propPosition.name ?? "Prop"}
          icon={<PropIcon size={20} />}
          onClick={() => props.onClick(targetId)}
        />
      );
    }),
    ...cameraPositions.map(({ position: cameraPosition, targetId }) => {
      return (
        <PiCreatorDropdownItem
          key={`camera-position-${targetId}`}
          text={cameraPosition.name ?? "Camera"}
          icon={<CameraAltOutlinedIcon fontSize="inherit" color="inherit" />}
          onClick={() => props.onClick(targetId)}
        />
      );
    }),
  ];

  const isPickerActive =
    sequencePositionPicker.isOpen &&
    sequencePositionPicker.nodeId === props.nodeId &&
    sequencePositionPicker.positionType === "lookatTarget" &&
    sequencePositionPicker.targetIndex === props.targetIndex;

  const pickerButtonClass = (isActive: boolean) =>
    NODE_PICK_BUTTON_BASE +
    (isActive
      ? NODE_PICK_BUTTON_ACTIVE
      : NODE_PICK_BUTTON_IDLE);

  const trigger = useMemo(
    () => (
      <div className="flex items-center w-full h-full gap-[10px] px-[4px] text-creator-text-sub text-[20px]">
        {selectedActor ? (
          <ActorAvatar
            theme="creator-selected"
            actor={selectedActor}
            size="24"
          />
        ) : selectedCameraPosition ? (
          <CameraAltOutlinedIcon fontSize="inherit" color="inherit" />
        ) : selectedActorPosition ? (
          <ActorAvatar theme="creator-selected" actor={undefined} size="24" />
        ) : isNoActor ? (
          <PersonOffOutlinedIcon fontSize="inherit" color="inherit" />
        ) : isKeepCurrent ? (
          <RemoveOutlinedIcon fontSize="inherit" color="inherit" />
        ) : (
          <PropIcon size={20} />
        )}
        <div className="my-auto font-normal text-[13px] text-creator-text">
          {selectedActor?.workingName ??
            selectedActorPosition?.position.name ??
            selectedPropPosition?.position.name ??
            selectedCameraPosition?.position.name ??
            emptyLabel}
        </div>
      </div>
    ),
    [
      selectedActor,
      selectedActorPosition,
      selectedCameraPosition,
      selectedPropPosition,
      emptyLabel,
      isNoActor,
      isKeepCurrent,
    ],
  );

  return (
    <div className="flex flex-col gap-[8px] noDrag nowheel">
      <div className="font-medium text-creator-text-sub text-[14px]">
        {props.label}
      </div>
      <div className="flex items-center gap-[8px]">
        <div className="flex-1 min-w-0">
          <PiCreatorDropdown
            style="square"
            trigger={trigger}
            items={dropdownItems as JSX.Element[]}
            disabled={props.disabled}
          />
        </div>
        <button
          type="button"
          aria-label="Pick on map"
          title="Pick on map"
          disabled={props.disabled}
          data-testid={
            props.targetIndex === undefined
              ? "pick-lookat-target"
              : `pick-lookat-target-${props.targetIndex}`
          }
          className={`${pickerButtonClass(isPickerActive)} ${
            props.disabled ? "opacity-60 cursor-not-allowed" : ""
          }`}
          onClick={() =>
            dispatch(
              openSequencePositionPicker({
                nodeId: props.nodeId,
                positionType: "lookatTarget",
                title: "Pick lookat target",
                presentation: "splitView",
                targetIndex: props.targetIndex,
              }),
            )
          }
        >
          <ColorizeIcon fontSize="inherit" className="text-[18px]" />
        </button>
      </div>
    </div>
  );
}
